import React from 'react';
import { StyledCheckIcon, StyledCrossIcon, StyledOption, StyledOptionList } from './PlanStyles';
import { PlansIds, SUBSCRIPTION_PERIOD, upgradePlansOrder } from '../../constants/config';
import { isMonthlyInterval, isYearlyInterval } from '../../utils/user';
import { PlanWithInterval } from '../Promo/types';

enum ButtonText {
  CHANGE_PLAN = 'Change Plan',
  UPGRADE_NOW = 'Upgrade Now',
  CURRENT_PACKAGE = 'Current Package',
  START_FREE_TRIAL = 'Start Free Trial',
  COMING_SOON = 'Coming Soon',
  BUY_NOW = 'Buy Now',
  EMPTY = '',
}

export const getButtonText = ({
  isCurrentPlan,
  isAuthorized,
  isTrial,
  isLoading,
  isDisabled,
  currentUserPlan,
  isCurrentInterval,
  isLifetime,
}): ButtonText => {
  if (isTrial) return ButtonText.UPGRADE_NOW;

  if (
    isCurrentPlan &&
    (isCurrentInterval ||
      // for free package we don't have interval
      currentUserPlan === PlansIds.FREE)
  ) {
    return ButtonText.CURRENT_PACKAGE;
  }

  if (isDisabled) {
    return ButtonText.COMING_SOON;
  }

  switch (currentUserPlan) {
    case PlansIds.STANDARD_PLUS:
    case PlansIds.APPSUMO_TIER1:
    case PlansIds.APPSUMO_TIER2:
    case PlansIds.APPSUMO_TIER3:
    case PlansIds.FREE:
    case PlansIds.ZERO:
      return ButtonText.UPGRADE_NOW;
  }

  if (isLifetime) return ButtonText.BUY_NOW;
  if (!isAuthorized) return ButtonText.START_FREE_TRIAL;
  if (isLoading) return ButtonText.EMPTY;

  return ButtonText.CHANGE_PLAN;
};

export const getPlanOptionsListByType = (type, options) =>
  options.map((option, optionIndex) => {
    if (Array.isArray(option)) {
      return (
        <StyledOptionList key={optionIndex}>
          {option.map((value, index) => (
            <StyledOption key={index}>{value}</StyledOption>
          ))}
        </StyledOptionList>
      );
    };

    const { value, comingSoon = false } = typeof option === 'object' ? option : { value: option };

    return (
      <StyledOption key={optionIndex} checked>
        {type === 'available' ? <StyledCheckIcon /> : <StyledCrossIcon />}
        {value}
        {comingSoon && <sup>coming soon</sup>}
      </StyledOption>
    );
  });

export const formatPriceWithCurrency = (currencySymbol) => (price) => {
  return [`${currencySymbol}${Math.floor(price)}`, (price % 1).toFixed(2).slice(2)];
};

/**
 * @param price {number}
 * @param discount {number} is percentage discount
 * @returns {number}
 */
export const getPriceWithDiscount = (price, discount) => {
  return price > 0 ? (price * (100 - discount)) / 100 : 0;
};

export function isDowngrade(currentPlan: PlansIds, nextPlan: PlansIds): Boolean {
  const currentPlanIndex = upgradePlansOrder.indexOf(currentPlan);
  const nextPlanIndex = upgradePlansOrder.indexOf(nextPlan);
  if (currentPlanIndex === -1 || nextPlanIndex === -1) return false;

  return nextPlanIndex < currentPlanIndex;
}

export function isPlanUpgrade(currentPlan: PlansIds, nextPlan: PlansIds): boolean {
  const currentPlanIndex = upgradePlansOrder.indexOf(currentPlan);
  const nextPlanIndex = upgradePlansOrder.indexOf(nextPlan);
  if (currentPlanIndex === -1 || nextPlanIndex === -1) return false;

  return nextPlanIndex > currentPlanIndex;
}

export function isIntervalUpgrade(currentPlan: SUBSCRIPTION_PERIOD, nextPlan: SUBSCRIPTION_PERIOD): boolean {
  return isMonthlyInterval(currentPlan) && isYearlyInterval(nextPlan);
}

export function isUpgrade(current: PlanWithInterval, next: PlanWithInterval): boolean {
  if (isIntervalUpgrade(current.interval, next.interval)) {
    return true;
  }

  if (current.interval === next.interval) {
    return isPlanUpgrade(current.planId, next.planId);
  }

  return false;
}
